import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AddFragranceModal from './AddFragranceModal';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

// Module-level cache so profile is only fetched once per session
let _profileCache = null;

const ExpandModal = ({ item, onClose, token, onNavigate, mode = 'library' }) => {
  const isMobile = useIsMobile();
  const [showAddModal, setShowAddModal] = useState(false);
  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(true);
  const [wishlisted, setWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const API = process.env.REACT_APP_API_URL;

  // Fetch owners
  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const res = await axios.get(
          `${API}/api/fragrances/${item.id}/owners`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOwners(res.data);
      } catch (err) { console.error(err); }
      setLoadingOwners(false);
    };
    if (item.id) fetchOwners();
  }, [item.id, token]); // eslint-disable-line

  // Fetch profile (cached per session)
  useEffect(() => {
    if (!token) return;
    const fetchProfile = async () => {
      if (_profileCache) { setUserProfile(_profileCache); return; }
      try {
        const res = await axios.get(`${API}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        _profileCache = res.data;
        setUserProfile(res.data);
      } catch (err) { console.error(err); }
    };
    fetchProfile();
  }, [token]); // eslint-disable-line

  // Check wishlist status if in wishlist mode
  useEffect(() => {
    if (mode !== 'wishlist' || !token) return;
    const checkWishlist = async () => {
      try {
        const res = await axios.get(`${API}/api/wishlist`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlisted(res.data.map(i => i.fragrance_id).includes(item.id));
      } catch (err) { console.error(err); }
    };
    checkWishlist();
  }, [item.id, mode, token]); // eslint-disable-line

  const handleWishlistToggle = async () => {
    setWishlistLoading(true);
    try {
      if (wishlisted) {
        await axios.delete(`${API}/api/wishlist/fragrance/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlisted(false);
      } else {
        await axios.post(`${API}/api/wishlist`, { fragrance_id: item.id }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlisted(true);
      }
    } catch (err) { console.error(err); }
    setWishlistLoading(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const parseNotes = (notes) => {
    if (!notes) return [];
    if (Array.isArray(notes)) return notes;
    try { return JSON.parse(notes); } catch { return [notes]; }
  };

  const topNotes  = parseNotes(item.top_notes);
  const midNotes  = parseNotes(item.middle_notes);
  const baseNotes = parseNotes(item.base_notes);
  const seasons   = Array.isArray(item.season)   ? item.season   : [];
  const occasions = Array.isArray(item.occasion) ? item.occasion : [];

  // ── User taste matching ────────────────────────────────────────────────────
  const favNotes   = userProfile?.profile?.favourite_notes   || [];
  const favAccords = userProfile?.profile?.favourite_accords || [];

  const allFragranceNotes = [...topNotes, ...midNotes, ...baseNotes];
  const matchingNotes     = allFragranceNotes.filter(n => favNotes.includes(n));
  const accordMatch       = favAccords.includes(item.main_accord);
  const showMatchBadge    = matchingNotes.length + (accordMatch ? 1 : 0) >= 2;

  const ratingDots = (value, max = 5) =>
    Array.from({ length: max }, (_, i) => (
      <span key={i} style={{
        display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%',
        background: i < value ? '#c9a96e' : '#2a2a2a', marginRight: '5px',
      }} />
    ));

  const tag = (label, i) => (
    <span key={i} style={{
      display: 'inline-block', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem',
      letterSpacing: '0.15em', textTransform: 'uppercase', color: '#a09890',
      border: '1px solid #2a2a2a', padding: '4px 10px', marginRight: '6px', marginBottom: '6px',
    }}>{label}</span>
  );

  const sectionLabel = {
    fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', fontWeight: 400,
    letterSpacing: '0.35em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '14px',
  };
  const noteGroupLabel = {
    fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', fontWeight: 300,
    letterSpacing: '0.2em', textTransform: 'uppercase', color: '#665e58', marginBottom: '8px',
  };
  const ratingLabel = {
    fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, color: '#a09890',
  };

  const pad       = isMobile ? '24px 20px' : '32px 40px';
  const headerPad = isMobile ? '24px 20px 20px' : '36px 40px 28px';

  // Note item with optional gold highlight
  const NoteItem = ({ note }) => {
    const isFav = favNotes.includes(note);
    return (
      <span style={{
        fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', fontWeight: 300,
        color: isFav ? '#c9a96e' : '#f5f0eb',
        marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px',
      }}>
        {isFav && <span style={{ fontSize: '0.45rem', color: '#c9a96e', flexShrink: 0 }}>✦</span>}
        {note}
      </span>
    );
  };

  const RightContent = () => (
    <>
      {(item.longevity || item.sillage) && (
        <div>
          <p style={sectionLabel}>Performance</p>
          {item.longevity && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={ratingLabel}>Longevity</span>
              <div>{ratingDots(item.longevity)}</div>
            </div>
          )}
          {item.sillage && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={ratingLabel}>Projection</span>
              <div>{ratingDots(item.sillage)}</div>
            </div>
          )}
        </div>
      )}
      {(seasons.length > 0 || occasions.length > 0) && (
        <div>
          <p style={sectionLabel}>Best Worn</p>
          <div style={{ marginBottom: '6px' }}>{seasons.map((s, i) => tag(s, i))}</div>
          <div>{occasions.map((o, i) => tag(o, i))}</div>
        </div>
      )}
      {item.country && (
        <div>
          <p style={sectionLabel}>Origin</p>
          <p style={{ ...ratingLabel, color: '#f5f0eb' }}>{item.country}</p>
        </div>
      )}
    </>
  );

  const NotesContent = () => (
    <>
      <p style={sectionLabel}>Fragrance Notes</p>
      {favNotes.length > 0 && matchingNotes.length > 0 && (
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.15em', color: '#665e58', marginBottom: '14px' }}>
          ✦ your favourite notes
        </p>
      )}
      <div style={{ display: 'flex', gap: isMobile ? '16px' : '24px' }}>
        {topNotes.length > 0 && (
          <div style={{ flex: 1 }}>
            <p style={noteGroupLabel}>Top</p>
            {topNotes.map((n, i) => <NoteItem key={i} note={n} />)}
          </div>
        )}
        {midNotes.length > 0 && (
          <div style={{ flex: 1 }}>
            <p style={noteGroupLabel}>Heart</p>
            {midNotes.map((n, i) => <NoteItem key={i} note={n} />)}
          </div>
        )}
        {baseNotes.length > 0 && (
          <div style={{ flex: 1 }}>
            <p style={noteGroupLabel}>Base</p>
            {baseNotes.map((n, i) => <NoteItem key={i} note={n} />)}
          </div>
        )}
      </div>
    </>
  );

  const FooterCTA = () => {
    if (mode === 'wishlist') {
      return (
        <button
          onClick={handleWishlistToggle}
          disabled={wishlistLoading}
          onMouseEnter={e => { if (!wishlisted) { e.currentTarget.style.background = '#b8965a'; } }}
          onMouseLeave={e => { if (!wishlisted) { e.currentTarget.style.background = '#c9a96e'; } }}
          style={{
            background: wishlisted ? 'none' : '#c9a96e',
            border: wishlisted ? '1px solid #c9a96e' : 'none',
            color: wishlisted ? '#c9a96e' : '#0a0a0a',
            fontFamily: 'Jost, sans-serif', fontSize: '0.65rem',
            letterSpacing: '0.2em', textTransform: 'uppercase',
            padding: '12px 20px', cursor: wishlistLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease', flex: isMobile ? 1 : 'none',
            opacity: wishlistLoading ? 0.6 : 1,
          }}
        >
          {wishlistLoading ? '...' : wishlisted ? '♥ Remove from Wishlist' : '♡ Save to Wishlist'}
        </button>
      );
    }
    return (
      <button
        onClick={() => setShowAddModal(true)}
        onMouseEnter={e => { e.currentTarget.style.background = '#b8965a'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#c9a96e'; }}
        style={{
          background: '#c9a96e', border: 'none', color: '#0a0a0a',
          fontFamily: 'Jost, sans-serif', fontSize: '0.65rem',
          letterSpacing: '0.2em', textTransform: 'uppercase',
          padding: '12px 20px', cursor: 'pointer',
          transition: 'background 0.2s ease', flex: isMobile ? 1 : 'none',
        }}
      >
        + Add to My Library
      </button>
    );
  };

  return (
    <>
      <div
        onClick={handleOverlayClick}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '0' : '24px', backdropFilter: 'blur(4px)',
        }}
      >
        <div style={{
          background: '#111111',
          border: isMobile ? 'none' : '1px solid #2a2a2a',
          width: '100%', maxWidth: '780px', position: 'relative',
          display: 'flex', flexDirection: 'column',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh',
          overflowY: 'auto',
        }}>

          {/* Close */}
          <button onClick={onClose} style={{
            position: 'absolute', top: '16px', right: '16px', background: 'none',
            border: 'none', color: '#a09890', fontSize: '1.1rem', cursor: 'pointer', zIndex: 1,
          }}>✕</button>

          {/* Header */}
          <div style={{ padding: headerPad, borderBottom: '1px solid #2a2a2a' }}>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '8px' }}>{item.brand}</p>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.7rem' : '2rem', fontWeight: 300, color: '#f5f0eb', lineHeight: 1.1, marginBottom: '8px', paddingRight: '36px' }}>{item.name}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {item.type && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 300, color: '#a09890', letterSpacing: '0.1em' }}>{item.type}</span>}
              {/* Accord tag — highlighted if it matches user's favourites */}
              {item.main_accord && (
                <span style={{
                  display: 'inline-block', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem',
                  letterSpacing: '0.15em', textTransform: 'uppercase',
                  color: accordMatch ? '#c9a96e' : '#a09890',
                  border: `1px solid ${accordMatch ? '#c9a96e' : '#2a2a2a'}`,
                  background: accordMatch ? 'rgba(201,169,110,0.08)' : 'transparent',
                  padding: '4px 10px',
                }}>
                  {accordMatch && <span style={{ marginRight: '4px' }}>✦</span>}
                  {item.main_accord}
                </span>
              )}
              {item.year_released && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 300, color: '#665e58' }}>{item.year_released}</span>}
              {item.price_gbp && <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem', fontWeight: 300, color: '#c9a96e' }}>£{item.price_gbp}</span>}
              {/* Matches your taste badge */}
              {showMatchBadge && (
                <span style={{
                  fontFamily: 'Jost, sans-serif', fontSize: '0.55rem',
                  letterSpacing: '0.2em', textTransform: 'uppercase',
                  color: '#0a0a0a', background: '#c9a96e',
                  padding: '3px 10px',
                }}>
                  Matches your taste
                </span>
              )}
            </div>
          </div>

          {/* Why this fragrance banner */}
          {item.reason && (
            <div style={{ padding: isMobile ? '16px 20px' : '20px 40px', background: 'rgba(201, 169, 110, 0.06)', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span style={{ color: '#c9a96e', fontSize: '1.2rem', flexShrink: 0, lineHeight: 1.4 }}>✦</span>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: 300, color: '#f5f0eb', fontStyle: 'italic' }}>{item.reason}</p>
            </div>
          )}

          {/* Body */}
          {isMobile ? (
            <div>
              <div style={{ padding: pad, borderBottom: '1px solid #2a2a2a' }}>
                <NotesContent />
              </div>
              <div style={{ padding: pad, borderBottom: '1px solid #2a2a2a', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <RightContent />
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr' }}>
              <div style={{ padding: pad }}><NotesContent /></div>
              <div style={{ background: '#2a2a2a' }} />
              <div style={{ padding: pad, display: 'flex', flexDirection: 'column', gap: '24px' }}><RightContent /></div>
            </div>
          )}

          {/* Who Owns This */}
          <div style={{ padding: isMobile ? '24px 20px' : '28px 40px', borderTop: '1px solid #2a2a2a' }}>
            <p style={sectionLabel}>In Others' Collections</p>
            {loadingOwners ? (
              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', color: '#3a3a3a', letterSpacing: '0.05em' }}>Loading...</p>
            ) : owners.length === 0 ? (
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', fontStyle: 'italic', color: '#3a3a3a' }}>
                No one else owns this yet — be the first.
              </p>
            ) : (
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                {owners.map(owner => (
                  <div
                    key={owner.id}
                    onClick={() => { if (onNavigate) { onNavigate(`/u/${owner.username}`); onClose(); } }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', cursor: onNavigate ? 'pointer' : 'default' }}
                  >
                    <div
                      style={{ width: '40px', height: '40px', borderRadius: '50%', background: owner.avatar_color || '#c9a96e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cormorant Garamond, serif', fontSize: '0.95rem', fontWeight: 500, color: '#0a0a0a', border: '1px solid #2a2a2a', transition: 'border-color 0.2s ease' }}
                      onMouseEnter={e => { if (onNavigate) e.currentTarget.style.borderColor = '#c9a96e'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; }}
                    >
                      {owner.username?.slice(0, 2).toUpperCase()}
                    </div>
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.1em', color: '#6b6460' }}>
                      {owner.username ? owner.username.charAt(0).toUpperCase() + owner.username.slice(1) : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: isMobile ? '16px 20px' : '20px 40px',
            borderTop: '1px solid #2a2a2a',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
          }}>
            <button onClick={onClose} style={{
              background: 'none', border: '1px solid #2a2a2a', color: '#a09890',
              fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em',
              textTransform: 'uppercase', padding: '12px 20px', cursor: 'pointer',
              flex: isMobile ? 1 : 'none',
            }}>Close</button>
            <FooterCTA />
          </div>
        </div>
      </div>

      {showAddModal && (
        <AddFragranceModal
          token={token}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); onClose(); }}
          preselected={item}
        />
      )}
    </>
  );
};

export default ExpandModal;